import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from "react-redux"
import "./index.css"

//Common, helpers
import Loading from '../../common/Loading'
import { Button, DropDownField, TextInputField } from '../../helpers'

//components
import Manufacture from './Components/Manufacture'
import ReverseManufacture from './Components/ReverseManufacture'

//services
import { insertManufactureStockAdjustment } from '../../services/api/stockAdjustment'
import { raiseToast } from '../../services/redux/reducers/toast/toast'
import { fetchCompanyWarehouses } from '../../services/redux/reducers/commonThunk'
import { getMaterialManufactureData, getConsumableInStockDetails, getSmartStockMovesForManufacture, getSmartStockMovesForReverseManufacture } from '../../services/api/materialManufacture'
import { getMaterialMasterAndTempMaterialIds } from '../../services/api/materialMaster'
import { fetchMaterialBrands, fetchMaterialCategories, fetchMaterialUoms } from '../../services/redux/reducers/commonThunk'

//validations
import { getProductName, validateInputs } from '../../utils'
import { MaterialManufactureValidationSchema } from '../../validation/materialManufactureValidation'

const MaterialManufacture = () => {

    //created material related
    const [createdMaterialIdList, setCreatedMaterialIdList] = useState([])
    const [selectedCreatedMaterialId, setSelectedCreatedMaterialId] = useState(null)
    const [companyWarehouseList, setCompanyWarehouseList] = useState([])
    const [selectedWarehouseId, setSelectedWarehouseId] = useState(null)
    const [uniqueCreatedMaterialIds, setUniqueCreatedMaterialIds] = useState([])

    //consumable in stock
    const [isVisibleConsumableDetails, setIsVisibleConsumableDetails] = useState(false)
    const [consumableInStockData, setConsumableInStockData] = useState([])

    //manufacture
    const [maxQtyToBeCreated, setMaxQtyToBeCreated] = useState(null)
    const [enteredQtyToManufacure, setEnteredQtyToManufacture] = useState(null)
    const [consumptionData, setConsumptionData] = useState([])
    const [creationData, setCreationData] = useState({})
    const [isConsumptionDataVisible, setIsConsumptionDataVisible] = useState(false)

    //Reverse Manufacture
    const [maxQtyToBeReversed, setMaxQtyToBeReversed] = useState(null)
    const [enteredQtyToReverse, setEnteredQtyToReverse] = useState(null)
    const [reversedSmartStockMoveData, setReversedSmartStockMoveData] = useState([])
    const [isReverseManufacture, setIsReverseManufacture] = useState(false)
    const [avlManufacturedQtyData, setAvlManufacturedQtyData] = useState([])

    // loading
    const [isLoading, setIsLoading] = useState(false)
    const [formErrors, setFormErrors] = useState({})
    const [errorMessage, setErrorMessage] = useState({
        createErrorMessage: '',
        reverseErrorMessage: ''
    })

    const companyWarehouses = useSelector(state => state.common.companyWarehouses)
    const materialCategories = useSelector((state) => state.common.materialCategories)
    const materialBrands = useSelector((state) => state.common.materialBrands)

    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(fetchCompanyWarehouses())
        dispatch(fetchMaterialCategories())
        dispatch(fetchMaterialBrands())
        dispatch(fetchMaterialUoms())
    }, [])

    useEffect(() => {
        if (materialBrands.length && materialCategories.length){
            getMaterialManufacturingData()
        }
    }, [materialBrands, materialCategories])

    useEffect(() => {
        if (companyWarehouses && companyWarehouses.length > 0) {
            setCompanyWarehouseList(companyWarehouses)
        }
    }, [companyWarehouses])

    useEffect(() => {
        const updateConsumableData = consumableInStockData.map(eachStock => {
            const requiredQty = enteredQtyToManufacure * eachStock.updatedConsumedRatio
            const remainingQty = eachStock?.smartStock - requiredQty
            return {
                ...eachStock,
                requiredQty: requiredQty.toFixed(3),
                remainingQty: remainingQty.toFixed(3)
            }
        })
        setConsumableInStockData(updateConsumableData)
    }, [enteredQtyToManufacure])

    useEffect(() =>{
        if (reversedSmartStockMoveData.length > 0) {
            const updateData = reversedSmartStockMoveData.map((eachRevSmartStockList) => {
                const relatedConsmedMaterial = consumableInStockData.find(eachConsumableMaterial => eachConsumableMaterial.materialId === eachRevSmartStockList[0].materialId);
                const placeholderQty = enteredQtyToReverse * relatedConsmedMaterial.updatedConsumedRatio

                return eachRevSmartStockList.map((eachSmartStockMove) => {
                    return { ...eachSmartStockMove, placeholderQty: placeholderQty.toFixed(3), adjustQty: null }
                })
            })

            setReversedSmartStockMoveData(updateData)
        }
    },[enteredQtyToReverse])

    const handleSubmit = async () => {
        setErrorMessage('')
        setReversedSmartStockMoveData([])
        setIsVisibleConsumableDetails(false)
        setIsReverseManufacture(false)
        let consumedMaterialIds = createdMaterialIdList.filter(each => each.createdMaterialId === selectedCreatedMaterialId)
            .map(each => each.consumedMaterialId)
        console.log("consumedMaterialIds", consumedMaterialIds)
        const data = {
            warehouseId: selectedWarehouseId,
            materialIds: consumedMaterialIds,
            selectedCreatedMaterialId: selectedCreatedMaterialId
        }
        const validation_res = await validateInputs(MaterialManufactureValidationSchema, data)
        if (validation_res.error) {
            setFormErrors(validation_res.error)
        }
        else {
            setFormErrors({})
            setIsLoading(true)
            setIsConsumptionDataVisible(false)
            const response = await getConsumableInStockDetails(data)
            setIsLoading(false)
            if (!response.failure) {
                let stockWarehouseData = response.data.stockWarehouseData

                let stockWarehouseMaterialIds = stockWarehouseData.map(item => item.materialId)
                consumedMaterialIds.forEach(materialId => {
                    if (!stockWarehouseMaterialIds.includes(materialId)) {
                        stockWarehouseData.push({ materialId: materialId, smartStock: 0 })
                    }
                })
                const updatedStockWarehouseMaterialIds = stockWarehouseData.map(eachStockWarehouseMaterial => eachStockWarehouseMaterial.materialId)

                if (stockWarehouseData.length > 0) {
                    let stockWarehouseMaterialDetails = await getMaterialMasterAndTempMaterialIds({ materialIds: updatedStockWarehouseMaterialIds })
                    stockWarehouseMaterialDetails = stockWarehouseMaterialDetails.data

                    const updatedStockWarehouseData = stockWarehouseData.map((eachStockWarehouseItem) => {
                        const relatedMaterialDetail = stockWarehouseMaterialDetails.find(eachMaterialDetails => eachMaterialDetails.materialId === eachStockWarehouseItem.materialId)

                        const relatedCreatedMaterial = createdMaterialIdList.find(createdMaterial => createdMaterial.consumedMaterialId == eachStockWarehouseItem.materialId && createdMaterial.createdMaterialId === selectedCreatedMaterialId)

                        const consumedMaterialRatio = relatedCreatedMaterial.consumedMaterialRatio
                        const updatedConsumedRatio = consumedMaterialRatio * (1 + (relatedCreatedMaterial.lossRatio / 100))
                        const relatedBrand = materialBrands.find(eachBrand => eachBrand.brandId === relatedMaterialDetail.brandId)
                        
                        let materialDetails = ''
                        if(relatedMaterialDetail){
                            materialDetails = `${eachStockWarehouseItem.materialId} - ${relatedBrand ? relatedBrand.brandName : ''} ${relatedMaterialDetail.productName} ${relatedMaterialDetail.variantName} ${relatedMaterialDetail.productSpecification} ${relatedMaterialDetail.UOMValue} ${relatedMaterialDetail.UOM}`
                        }

                        const requiredQty = enteredQtyToManufacure * updatedConsumedRatio
                        const remainingQty = eachStockWarehouseItem.smartStock - requiredQty
                        const maxManufactureQty = eachStockWarehouseItem.smartStock / parseFloat(updatedConsumedRatio)

                        return {
                            ...eachStockWarehouseItem,
                            materialDetails,
                            updatedConsumedRatio,
                            smartStock: parseFloat(eachStockWarehouseItem.smartStock),
                            maxManufactureQty: parseFloat(maxManufactureQty.toFixed(3)),
                            consumedMaterialRatio: parseFloat(consumedMaterialRatio),
                            lossRatio: parseFloat(relatedCreatedMaterial.lossRatio),
                            requiredQty: parseFloat(requiredQty.toFixed(3)),
                            remainingQty: parseFloat(remainingQty.toFixed(3))
                        }
                    })

                    setIsVisibleConsumableDetails(true)
                    setConsumableInStockData(updatedStockWarehouseData)

                    const minMaxManufactureQty = Math.min(...updatedStockWarehouseData.map(item => item.maxManufactureQty))
                    setMaxQtyToBeCreated(minMaxManufactureQty)
                    setEnteredQtyToManufacture(minMaxManufactureQty)

                    let manufacturedQtyData = response.data.manufacturedQtyData
                    setAvlManufacturedQtyData(manufacturedQtyData)
                    const totalManufacturedQty = manufacturedQtyData.reduce((sum, item) => {
                        return sum + (item.moveQty ? parseFloat(item.moveQty) : 0)
                    }, 0)

                    setMaxQtyToBeReversed(totalManufacturedQty)
                    setEnteredQtyToReverse(totalManufacturedQty)
                }
                else {
                    return dispatch(raiseToast({ severity: 'error', detail: "Consumables in stock are not available", life: 3000 }))
                }
            }
            else {
                return dispatch(raiseToast({ severity: 'error', detail: response?.data.error, life: 3000 }))
            }
        }
    }

    const getMaterialManufacturingData = async () => {
        const response = await getMaterialManufactureData()
        if (!response.failure) {
            const data = response.data
            setCreatedMaterialIdList(data)
            const uniqueCreatedMaterialIdsList = data.reduce((acc, curr) => {
                if (!acc.find(item => item.createdMaterialId === curr.createdMaterialId)) {
                    acc.push({ createdMaterialId: curr.createdMaterialId })
                }
                return acc
            }, [])

            const createdMaterialIds = uniqueCreatedMaterialIdsList.map(each => each.createdMaterialId).sort((a, b) => a - b)
            if (createdMaterialIds.length) {
                const response = await getMaterialMasterAndTempMaterialIds({ materialIds: createdMaterialIds })
                if(!response.failure){
                    const createdMaterialsWithMaterialDetails = response.data ? response.data : []
                    const updateCreatedMaterialDetails = createdMaterialIds.map((createdMaterialId) => {
                        const relatedCreatedMaterialDetail = createdMaterialsWithMaterialDetails.find(eachMaterialDetails => eachMaterialDetails.materialId === createdMaterialId)
                        const relatedCategory = materialCategories.find(eachCategory => eachCategory.categoryId === relatedCreatedMaterialDetail.categoryId)
                        const relatedBrand = materialBrands.find(eachBrand => eachBrand.brandId === relatedCreatedMaterialDetail.brandId)
                        const materialDetails = `${relatedCreatedMaterialDetail.materialId} - ${relatedBrand?.brandName} ${relatedCreatedMaterialDetail.productName} ${relatedCreatedMaterialDetail.variantName} ${relatedCreatedMaterialDetail.productSpecification} ${relatedCreatedMaterialDetail.UOMValue} ${relatedCreatedMaterialDetail.UOM}`
                        return {
                            ...relatedCreatedMaterialDetail,
                            relatedCategory,
                            relatedBrand,
                            materialDetails
                        }
                    })
                    setUniqueCreatedMaterialIds(updateCreatedMaterialDetails)
                }else{
                    return dispatch(raiseToast({ severity: 'error', detail: response.data.error, life: 3000 }))
                }
            }else{
                return dispatch(raiseToast({ severity: 'error', detail: "No material found to manufacture", life: 3000 }))
            }

        }
    }

    const handleReverseManufacture = async () => {
        setIsConsumptionDataVisible(false)
        setIsReverseManufacture(false)
        if (enteredQtyToReverse && enteredQtyToReverse > 0) {
            let consumedMaterialIds = consumableInStockData.map(eachConsumableMaterial => eachConsumableMaterial.materialId)
            const data = { materialIds: consumedMaterialIds }
            setIsLoading(true)
                const response = await getSmartStockMovesForReverseManufacture(data)
                if (!response.failure) {
                    const smartStockMovesToReverse = response.data.smartStockMovesToReverse
                    const sellerPoQtyData = response.data.sellerPoQtyData

                    const smartStockMovesToReverseData = []

                    consumableInStockData.forEach(eachConsumedMaterial => {
                        const relatedSmartStockMovesToReverse = smartStockMovesToReverse.filter(eachSmartStockMove => eachSmartStockMove.materialId === eachConsumedMaterial.materialId)
                        const requiredQty = enteredQtyToReverse * eachConsumedMaterial.updatedConsumedRatio

                        relatedSmartStockMovesToReverse.forEach(eachSmartStockMove => {
                            const { sellerPODocNo, maxAvailableQty, price } = eachSmartStockMove
                            const sellerPOData = sellerPoQtyData.find(eachSellerPO => eachSellerPO.sellerpodocno === sellerPODocNo)
                            const sellerPOAvailableQty = sellerPOData?.totalqty || 0

                            smartStockMovesToReverseData.push({
                                materialId: eachConsumedMaterial.materialId,
                                sellerPODocNo: sellerPODocNo,
                                price: price,
                                sellerPoQty: Number(sellerPOAvailableQty),
                                placeholderQty: requiredQty.toFixed(3),
                                GSTRate: eachSmartStockMove.GSTRate,
                                maxAdjustQty: maxAvailableQty ? Math.abs(Number(maxAvailableQty)).toFixed(3) : 0
                            })
                        })
                    })

                    const groupedSmartStockMovesToReverse = smartStockMovesToReverseData.reduce((acc, curr) => {
                        const materialId = curr.materialId
                        const existingGroup = acc.find(group => group.length && group[0].materialId === materialId)

                        if (existingGroup) {
                            existingGroup.push(curr)
                        } else {
                            acc.push([curr])
                        }

                        return acc
                    }, [])
                    setReversedSmartStockMoveData(groupedSmartStockMovesToReverse)
                    setIsReverseManufacture(true)
                    setIsLoading(false)

                    return groupedSmartStockMovesToReverse

                } else {
                    setIsLoading(false)
                    return dispatch(raiseToast({ severity: 'error', detail: response.data.error, life: 3000 }))
                }
            }
        else {
            setIsLoading(false)
            return dispatch(raiseToast({ severity: 'warn', detail: 'Please enter a valid qty.', life: 3000 }))
        }

    }

    const handleManufacture = async () => {
        setIsConsumptionDataVisible(false)
        setIsReverseManufacture(false)
        if (enteredQtyToManufacure && enteredQtyToManufacure > 0) {
            setErrorMessage('')
            setIsLoading(true)
            if (enteredQtyToManufacure == null) {
                return dispatch(raiseToast({ severity: 'warn', detail: "Please enter qty.", life: 3000 }))
            }
            else if (enteredQtyToManufacure > maxQtyToBeCreated) {
                return dispatch(raiseToast({ severity: 'warn', detail: `Entered Qty Can't be more than ${maxQtyToBeCreated}`, life: 3000 }))
            }
            let consumedMaterialIds = consumableInStockData.map(eachConsumableMaterial => eachConsumableMaterial.materialId)
            const data = {
                warehouseId: selectedWarehouseId,
                materialIds: consumedMaterialIds
            }
            const response = await getSmartStockMovesForManufacture(data)
            if (!response.failure) {
                const avalSmartStockMoves = response.data
                const materialIdsWithoutSmartStockMoves = []
                const insufficientQtyMaterialIds = []
                const consumptionData = consumableInStockData.map(eachConsumableMaterial => {
                    let requiredQty = eachConsumableMaterial.requiredQty
                    const relatedSmartStockMoves = avalSmartStockMoves.filter(eachSmartStockMove => eachSmartStockMove.materialId === eachConsumableMaterial.materialId)

                    if (relatedSmartStockMoves.length === 0) {
                        materialIdsWithoutSmartStockMoves.push(eachConsumableMaterial.materialId)
                        return []
                    }

                    let totalAvailableQty = 0
                    relatedSmartStockMoves.forEach(stockMove => {
                        totalAvailableQty += parseFloat(stockMove.moveQty)
                    })

                    if (totalAvailableQty < requiredQty) {
                        insufficientQtyMaterialIds.push(eachConsumableMaterial.materialId)
                        return []
                    }
                    const results = []
                    let i = 0
                    while (requiredQty > 0 && i < relatedSmartStockMoves.length) {
                        const eachSmartStockMove = relatedSmartStockMoves[i]
                        const moveQty = parseFloat(eachSmartStockMove.moveQty)
                        const price = parseFloat(eachSmartStockMove.price)

                        if (requiredQty <= moveQty) {
                            results.push({
                                materialId: eachConsumableMaterial.materialId,
                                sellerPoNo: parseFloat(eachSmartStockMove.sellerPODocNo),
                                qty: requiredQty,
                                price: price,
                                purchaseAmount: requiredQty * price,
                                lossRatio: eachConsumableMaterial.lossRatio,
                                sellerPOId: eachSmartStockMove.sellerPOId
                            })
                            break

                        } else {
                            results.push({
                                materialId: eachConsumableMaterial.materialId,
                                sellerPoNo: parseFloat(eachSmartStockMove.sellerPODocNo),
                                qty: moveQty,
                                price: price,
                                purchaseAmount: moveQty * price,
                                lossRatio: eachConsumableMaterial.lossRatio,
                                sellerPOId: eachSmartStockMove.sellerPOId
                            })
                            requiredQty -= moveQty
                        }
                        i++
                    }
                    return results
                })
                if (materialIdsWithoutSmartStockMoves.length > 0) {
                    setIsLoading(false)
                    return dispatch(raiseToast({ severity: 'error', detail: `SmartStock moves data not found for materialIds: ${materialIdsWithoutSmartStockMoves.join(', ')}`, life: 3000 }))
                }
                if (insufficientQtyMaterialIds.length > 0) {
                    setIsLoading(false)
                    return dispatch(raiseToast({ severity: 'error', detail: `Insufficient quantity in SmartStock moves for materialIds: ${insufficientQtyMaterialIds.join(', ')}`, life: 3000 }))
                }

                if (consumptionData.length > 0) {
                    const totalPurchaseAmount = consumptionData.flatMap(item => item)
                        .reduce((sum, item) => sum + item.purchaseAmount, 0)

                    const maxPurchaseValueObject = consumptionData.flatMap(item => item)
                        .reduce((maxObj, item) => {
                            return item.purchaseAmount > maxObj.purchaseAmount ? item : maxObj
                        }, consumptionData.flatMap(item => item)[0])

                    const creationDetails = {
                        materialId: selectedCreatedMaterialId,
                        sellerPONo: maxPurchaseValueObject.sellerPoNo,
                        createdQty: enteredQtyToManufacure,
                        price: totalPurchaseAmount / enteredQtyToManufacure,
                        amount: totalPurchaseAmount
                    }

                    setIsConsumptionDataVisible(true)
                    setCreationData(creationDetails)
                    setConsumptionData(consumptionData)
                    setIsLoading(false)
                }
                else {
                    setIsLoading(false)
                    return dispatch(raiseToast({ severity: 'warn', detail: 'No consumption details available.', life: 3000 }))
                }
            } else {
                setIsLoading(false)
                return dispatch(raiseToast({ severity: 'error', detail: response.data.error, life: 3000 }))
            }
        }
        else {
            return dispatch(raiseToast({ severity: 'warn', detail: 'Please enter a valid qty.', life: 3000 }))
        }
    }

    const handleStockAdjForReverseManufacture = async (reversedStockAdjObjects) => {
        const data = {
            stockAdjustment: reversedStockAdjObjects
        }

        if (window.confirm("It will trigger reverse manufacture. Are you sure to confirm?")) {
            setIsLoading(true)
            const stockAdjustmentRes = await insertManufactureStockAdjustment(data)
            setIsLoading(false)
            if (!stockAdjustmentRes?.failure) {
                setIsConsumptionDataVisible(false)
                setIsVisibleConsumableDetails(false)
                setSelectedCreatedMaterialId(null)
                setSelectedWarehouseId(null)
                setIsReverseManufacture(false)
                return dispatch(raiseToast({ severity: 'success', detail: 'Manufactured material is successfully Reversed.', life: 3000 }))
            }
            else {
                return dispatch(raiseToast({ severity: 'error', detail: stockAdjustmentRes?.data.error, life: 3000 }))
            }
        }
    }

    const handleStockAdjForManufacture = async (stockAdjObjects) => {
        const data = {
            stockAdjustment: stockAdjObjects
        }
        if (window.confirm("Are you sure to confirm?")) {
            setIsLoading(true)
            const response = await insertManufactureStockAdjustment(data)
            setIsLoading(false)
            if (!response.failure) {
                setIsConsumptionDataVisible(false)
                setIsVisibleConsumableDetails(false)
                setSelectedCreatedMaterialId(null)
                setSelectedWarehouseId(null)
                return dispatch(raiseToast({ severity: 'success', detail: 'Material is successfully manufactured.', life: 3000 }))
            }
            else {
                return dispatch(raiseToast({ severity: 'error', detail: response.data.error, life: 3000 }))
            }
        }
    }

    const handleQtyChange = (e) => {
        const value = e.target.value
        if (!isNaN(value)) {
            if (value > maxQtyToBeCreated) {
                setEnteredQtyToManufacture(maxQtyToBeCreated.toString())
                setErrorMessage(prevState => ({ ...prevState, createErrorMessage: 'Entered Qty exceeds the maximum limit' }))
            } else {
                setErrorMessage(prevState => ({ ...prevState, createErrorMessage: '' }))
                setEnteredQtyToManufacture(value)
            }
        } else {
            setErrorMessage(prevState => ({ ...prevState, createErrorMessage: 'Value must be numeric' }))
        }
    }

    const handleReverseQtyChange = (e) => {
        const value = e.target.value
        if (!isNaN(value)) {
            if (value > maxQtyToBeReversed) {
                setEnteredQtyToReverse(maxQtyToBeReversed.toString())
                setErrorMessage(prevState => ({ ...prevState, reverseErrorMessage: 'Entered Qty exceeds the maximum limit' }))
            } else {
                setErrorMessage(prevState => ({ ...prevState, reverseErrorMessage: '' }))
                setEnteredQtyToReverse(value)
            }
        } else {
            setErrorMessage(prevState => ({ ...prevState, reverseErrorMessage: 'Value must be numeric' }))
        }
    }

    return (
        <div className='manufacture-modal'>
            <Loading visible={isLoading} />
            <div>
                <h3 className="m-2 ml-8" style={{ marginLeft: '20px' }}>MATERIAL MANUFACTURE</h3>
            </div>
            <div className='p-card'>
                <div className="grid">
                    <div className="flex flex-wrap col-12 pl-0">
                        <div className="flex flex-column col-12 mb-2 md:col-4 lg:col-6 lg:mb-0">
                            <DropDownField
                                labelText="Select Material to Manufacture"
                                options={uniqueCreatedMaterialIds}
                                optionLabel="materialDetails"
                                optionValue="materialId"
                                onChange={(e) => setSelectedCreatedMaterialId(e.target.value)}
                                value={selectedCreatedMaterialId}
                                required
                                errorMessage={formErrors.selectedCreatedMaterialId}
                            />
                        </div>
                        <div className="flex flex-column col-12 mb-6 md:col-4 lg:col-2 lg:mb-0">
                            <DropDownField
                                labelText="Select Warehouse"
                                options={companyWarehouseList}
                                optionLabel="warehouseName"
                                optionValue="warehouseId"
                                onChange={(e) => setSelectedWarehouseId(e.target.value)}
                                value={selectedWarehouseId}
                                required
                                errorMessage={formErrors.warehouseId}
                            />
                        </div>
                        <div className="flex flex-column justify-end mt-4">
                            <Button className="small-btn mr-2" label="Submit" onClick={handleSubmit} />
                        </div>
                    </div>
                </div>
            </div>
            {isVisibleConsumableDetails && (
                <div className='p-card mt-2'>
                    <p style={{ fontSize: '14px', fontWeight: 'bold' }}>CONSUMABLES IN STOCK </p>
                    <div className="p-card" style={{ width: '100%' }}>
                        <div className="custom-table-container">
                            <table className="custom-product-table">
                                <thead>
                                    <tr className="">
                                        <th className="">SR</th>
                                        <th className=''>Consumed Material Id</th>
                                        <th className=''>Available in Stock</th>
                                        <th className=''>Consumed Ratio</th>
                                        <th className=''> Max Manufacturing Qty</th>
                                        <th className=''> Required Qty</th>
                                        <th className=''> Remaining Qty</th>
                                    </tr>
                                </thead>
                                <tbody style={{ fontSize: "12px", borderCollapse: "collapse" }}>
                                    {consumableInStockData?.map((item, index) => (
                                        <tr className="" key={index} style={{ borderBottom: "1px solid #818181", borderCollapse: "collapse" }}>
                                            <td className="">{index + 1}</td>
                                            <td className="">{item?.materialDetails}</td>
                                            <td className="">{item?.smartStock}</td>
                                            <td className="">{item?.updatedConsumedRatio}</td>
                                            <td className="">{item?.maxManufactureQty || ''}</td>
                                            <td className="">{item?.requiredQty || ''}</td>
                                            <td className="">{item?.remainingQty || ''}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="flex flex-row col-12 pl-0" style={{ justifyContent: 'space-between', width: '100%' }}>
                            <div className="flex flex-row">
                                <div className="flex flex-column">
                                    <TextInputField labelText={`Enter Qty to Create - ${selectedCreatedMaterialId} (Max Qty To Be Created : ${maxQtyToBeCreated})`} value={enteredQtyToManufacure} onChange={(e) => handleQtyChange(e)} errorMessage={errorMessage.createErrorMessage} required />
                                </div>
                                <div className="flex flex-column mt-3 ml-2" style={{ marginLeft: "0" }}>
                                    <Button className="small-btn" label="Manufacture" onClick={handleManufacture} />
                                </div>
                            </div>

                            <div className="flex flex-row">
                                <div className="flex flex-column ml-5">
                                    <TextInputField labelText={`Enter Qty to Reverse - ${selectedCreatedMaterialId} (Max Qty To Be Reversed : ${maxQtyToBeReversed})`} value={enteredQtyToReverse} onChange={(e) => handleReverseQtyChange(e)} errorMessage={errorMessage.reverseErrorMessage} required />
                                </div>
                                <div className="flex flex-column mt-3 ml-2" style={{ marginLeft: "0", color: "black" }}>
                                    <Button className="small-btn p-button-danger" label="Reverse Manufacture" onClick={handleReverseManufacture} />
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            )}

            <Manufacture
                isConsumptionDataVisible={isConsumptionDataVisible}
                consumptionData={consumptionData}
                consumableInStockData={consumableInStockData}
                uniqueCreatedMaterialIds={uniqueCreatedMaterialIds}
                selectedCreatedMaterialId={selectedCreatedMaterialId}
                creationData={creationData}
                enteredQtyToManufacure={enteredQtyToManufacure}
                selectedWarehouseId={selectedWarehouseId}
                createdMaterialIdList={createdMaterialIdList}
                handleStockAdjForManufacture={handleStockAdjForManufacture}
            />

            <ReverseManufacture
                isReverseManufacture={isReverseManufacture}
                enteredQtyToReverse={enteredQtyToReverse}
                uniqueCreatedMaterialIds={uniqueCreatedMaterialIds}
                selectedCreatedMaterialId={selectedCreatedMaterialId}
                reversedSmartStockMoveData={reversedSmartStockMoveData}
                consumableInStockData={consumableInStockData}
                selectedWarehouseId={selectedWarehouseId}
                handleStockAdjForReverseManufacture={handleStockAdjForReverseManufacture}
                avlManufacturedQtyData={avlManufacturedQtyData}
                setReversedSmartStockMoveData={setReversedSmartStockMoveData}
            />

        </div>
    )
}

export default MaterialManufacture
